'use client';
import { Box } from '@mui/material';
import { CENTERED_ICON, COLORS } from '../../constants';
import { MaterialIcon, MaterialIconType } from '../materialIcon';

export type IconBadgeTone = 'brand' | 'info' | 'success' | 'warning' | 'error';

// color tints the rings; glyph is the tone darkened toward black so it doesn't blend into its own tints (as in mobile)
const TONES: Record<IconBadgeTone, { color: string; glyph: string }> = {
  brand: { color: COLORS.brand.main, glyph: COLORS.brand.dark },
  info: { color: COLORS.primary.blue.main, glyph: '#140C73' },
  success: { color: COLORS.primary.green.dark, glyph: '#014D00' },
  warning: { color: COLORS.state.warning, glyph: '#6C3A06' },
  error: { color: COLORS.state.error, glyph: '#760800' },
};

const ring = (size: number, color: string, children: React.ReactNode) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: '50%',
      backgroundColor: color,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}
  >
    {children}
  </Box>
);

/**
 * Dialog icon from customer-mobile: three concentric rings tinted from the tone, with a darkened glyph.
 *
 * @param {MaterialIconType} icon - Material Symbols icon name.
 * @param {IconBadgeTone} tone - Picks the ring and glyph colors.
 */
export const IconBadge = ({ icon, tone = 'brand' }: { icon: MaterialIconType; tone?: IconBadgeTone }) => {
  const { color, glyph } = TONES[tone];
  return (
    <Box aria-hidden="true" data-testid="icon-badge" sx={{ display: 'flex', justifyContent: 'center' }}>
      {ring(
        104,
        `${color}1A`,
        ring(
          88,
          `${color}26`,
          ring(64, `${color}40`, <MaterialIcon icon={icon} size={28} sx={{ ...CENTERED_ICON, color: glyph }} />),
        ),
      )}
    </Box>
  );
};
